// Budget utility functions for calculations and formatting

import type { ProjectHealth } from '@/types';

// ============================================================================
// FORMATTING
// ============================================================================

export function formatCurrency(amount: number | undefined | null, currency = '£'): string {
  if (amount === undefined || amount === null || isNaN(amount)) {
    return `${currency}0.00`;
  }
  return `${currency}${amount.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatPercent(value: number | undefined | null, decimals = 1): string {
  if (value === undefined || value === null || isNaN(value)) {
    return '0.0%';
  }
  return `${value.toFixed(decimals)}%`;
}

export function formatHours(hours: number | undefined | null): string {
  if (hours === undefined || hours === null || isNaN(hours)) {
    return '0.00';
  }
  return hours.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatVariance(variance: number | undefined | null, currency = '£'): string {
  if (variance === undefined || variance === null || isNaN(variance) || variance === 0) return '-';
  const sign = variance > 0 ? '+' : '';
  return `${sign}${formatCurrency(variance, currency)}`;
}

export function formatVariancePercent(variance: number | undefined | null): string {
  if (variance === undefined || variance === null || isNaN(variance) || variance === 0) return '-';
  const sign = variance > 0 ? '+' : '';
  return `${sign}${formatPercent(variance)}`;
}

// ============================================================================
// CALCULATIONS
// ============================================================================

export function calculateVariance(budget: number | undefined | null, actual: number | undefined | null): number {
  const b = budget ?? 0;
  const a = actual ?? 0;
  return b - a;
}

export function calculateVariancePercent(budget: number | undefined | null, actual: number | undefined | null): number {
  const b = budget ?? 0;
  const a = actual ?? 0;
  if (b === 0) return 0;
  return ((b - a) / b) * 100;
}

export function calculateProgressPercent(actual: number | undefined | null, budget: number | undefined | null): number {
  const a = actual ?? 0;
  const b = budget ?? 0;
  if (b === 0) return 0;
  return (a / b) * 100;
}

export function calculateMargin(revenue: number | undefined | null, costs: number | undefined | null): number {
  const r = revenue ?? 0;
  const c = costs ?? 0;
  if (r === 0) return 0;
  return ((r - c) / r) * 100;
}

export function calculateMarginAmount(revenue: number | undefined | null, costs: number | undefined | null): number {
  const r = revenue ?? 0;
  const c = costs ?? 0;
  return r - c;
}

// ============================================================================
// STATUS DETERMINATION
// ============================================================================

export function getCostStatus(
  progressPercent: number,
  targetPercent = 80
): 'green' | 'amber' | 'red' {
  if (progressPercent < targetPercent) return 'green';
  if (progressPercent < 100) return 'amber';
  return 'red';
}

export function getRevenueStatus(
  actualPercent: number,
  expectedPercent: number,
  tolerance = 10
): 'green' | 'amber' | 'red' {
  const difference = expectedPercent - actualPercent;
  if (difference <= tolerance) return 'green';
  if (difference <= tolerance * 2) return 'amber';
  return 'red';
}

/**
 * Get Tailwind CSS color class for variance display
 */
export function getVarianceColor(variance: number, isReversed = false): string {
  if (variance === 0) return 'text-muted-foreground';
  
  const isPositive = variance > 0;
  const isGood = isReversed ? !isPositive : isPositive;
  
  return isGood
    ? 'text-success-600 dark:text-success-500'
    : 'text-error-600 dark:text-error-500';
}

export function getMarginStatus(
  actualMargin: number,
  targetMargin: number,
  tolerance = 5
): 'green' | 'amber' | 'red' {
  const difference = targetMargin - actualMargin;
  if (difference <= 0) return 'green';
  if (difference <= tolerance) return 'amber';
  return 'red';
}

export function getHoursStatus(
  progressPercent: number,
  targetPercent = 80
): 'green' | 'amber' | 'red' {
  if (progressPercent < targetPercent) return 'green';
  if (progressPercent < 100) return 'amber';
  return 'red';
}

/**
 * Resource Budget Status Thresholds:
 * - GOOD: 0-79% of budget used (Green)
 * - WARNING: 80-99% of budget used (Amber/Yellow)
 * - OVER: 100%+ of budget used (Red)
 */
export type ResourceBudgetStatus = 'good' | 'warning' | 'over';

export function getResourceBudgetStatus(
  actualHours: number,
  budgetHours: number
): ResourceBudgetStatus {
  if (budgetHours === 0) return 'good';
  
  const percentUsed = (actualHours / budgetHours) * 100;
  
  if (percentUsed >= 100) return 'over';
  if (percentUsed >= 80) return 'warning';
  return 'good';
}

export function getResourceBudgetStatusLabel(status: ResourceBudgetStatus): string {
  switch (status) {
    case 'good':
      return 'On Track';
    case 'warning':
      return 'At Risk';
    case 'over':
      return 'Over Budget';
  }
}

export function getResourceBudgetStatusColor(status: ResourceBudgetStatus): {
  text: string;
  bg: string;
  border: string;
  badge: string;
} {
  switch (status) {
    case 'good':
      return {
        text: 'text-success-700 dark:text-success-400',
        bg: 'bg-success-50 dark:bg-success-950',
        border: 'border-success-200 dark:border-success-800',
        badge: 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300',
      };
    case 'warning':
      return {
        text: 'text-warning-700 dark:text-warning-400',
        bg: 'bg-warning-50 dark:bg-warning-950',
        border: 'border-warning-200 dark:border-warning-800',
        badge: 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-300',
      };
    case 'over':
      return {
        text: 'text-error-700 dark:text-error-400',
        bg: 'bg-error-50 dark:bg-error-950',
        border: 'border-error-200 dark:border-error-800',
        badge: 'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-300',
      };
  }
}

export function getResourceBudgetStatusIcon(status: ResourceBudgetStatus): string {
  switch (status) {
    case 'good':
      return '✓'; // Check mark
    case 'warning':
      return '⚠'; // Warning triangle
    case 'over':
      return '✕'; // X mark
  }
}

/**
 * Team Allocation Status Types:
 * - STAFFED: Allocated equals Budget
 * - UNFILLED: Allocated equals 0
 * - UNDER_STAFFED: Allocated is less than Budget (but greater than 0)
 * - OVER_STAFFED: Allocated is greater than Budget
 */
export type TeamAllocationStatus = 'staffed' | 'unfilled' | 'under-staffed' | 'over-staffed';

export function getTeamAllocationStatus(
  allocatedHours: number,
  budgetHours: number
): TeamAllocationStatus {
  if (allocatedHours === 0) return 'unfilled';
  if (allocatedHours === budgetHours) return 'staffed';
  if (allocatedHours < budgetHours) return 'under-staffed';
  return 'over-staffed';
}

export function getTeamAllocationStatusLabel(status: TeamAllocationStatus): string {
  switch (status) {
    case 'staffed':
      return 'Staffed';
    case 'unfilled':
      return 'Unfilled';
    case 'under-staffed':
      return 'Under-staffed';
    case 'over-staffed':
      return 'Over-staffed';
  }
}

export function getTeamAllocationStatusColor(status: TeamAllocationStatus): {
  text: string;
  bg: string;
  border: string;
  badge: string;
} {
  switch (status) {
    case 'staffed':
      return {
        text: 'text-success-700 dark:text-success-400',
        bg: 'bg-success-50 dark:bg-success-950',
        border: 'border-success-200 dark:border-success-800',
        badge: 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300',
      };
    case 'unfilled':
      return {
        text: 'text-error-700 dark:text-error-400',
        bg: 'bg-error-50 dark:bg-error-950',
        border: 'border-error-200 dark:border-error-800',
        badge: 'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-300',
      };
    case 'under-staffed':
      return {
        text: 'text-warning-700 dark:text-warning-400',
        bg: 'bg-warning-50 dark:bg-warning-950',
        border: 'border-warning-200 dark:border-warning-800',
        badge: 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-300',
      };
    case 'over-staffed':
      return {
        text: 'text-blue-700 dark:text-blue-400',
        bg: 'bg-blue-50 dark:bg-blue-950',
        border: 'border-blue-200 dark:border-blue-800',
        badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      };
  }
}

export function getBudgetHealth(
  costProgress: number,
  revenueProgress: number,
  actualMargin: number,
  targetMargin: number
): ProjectHealth {
  const costStatus = getCostStatus(costProgress);
  const marginStatus = getMarginStatus(actualMargin, targetMargin);
  
  // Red if cost is over budget OR margin is significantly below target
  if (costStatus === 'red' || marginStatus === 'red') {
    return 'red';
  }
  
  // Amber if cost is warning OR margin is below target
  if (costStatus === 'amber' || marginStatus === 'amber') {
    return 'amber';
  }
  
  // Green if everything is on track
  return 'green';
}

// ============================================================================
// TREND DETERMINATION
// ============================================================================

export function getTrend(
  current: number,
  previous: number,
  threshold = 5
): 'up' | 'down' | 'stable' {
  const percentChange = ((current - previous) / previous) * 100;
  
  if (Math.abs(percentChange) < threshold) return 'stable';
  return percentChange > 0 ? 'up' : 'down';
}

// ============================================================================
// FORECASTING
// ============================================================================

export function forecastFinalCost(
  actualCost: number,
  progressPercent: number
): number {
  if (progressPercent === 0) return 0;
  return (actualCost / progressPercent) * 100;
}

export function forecastFinalRevenue(
  actualRevenue: number,
  progressPercent: number
): number {
  if (progressPercent === 0) return 0;
  return (actualRevenue / progressPercent) * 100;
}

export function calculateBurnRate(
  actualCost: number,
  monthsElapsed: number
): number {
  if (monthsElapsed === 0) return 0;
  return actualCost / monthsElapsed;
}

export function estimateCompletionDate(
  budget: number,
  actualCost: number,
  monthsElapsed: number,
  startDate: Date
): Date | null {
  const burnRate = calculateBurnRate(actualCost, monthsElapsed);
  if (burnRate === 0) return null;
  
  const remainingBudget = budget - actualCost;
  const monthsRemaining = remainingBudget / burnRate;
  
  const completionDate = new Date(startDate);
  completionDate.setMonth(completionDate.getMonth() + monthsElapsed + monthsRemaining);
  
  return completionDate;
}

// ============================================================================
// VALIDATION
// ============================================================================

export function validateBudgetChange(
  currentBudget: number,
  newBudget: number,
  threshold = 5
): {
  isValid: boolean;
  requiresApproval: boolean;
  changePercent: number;
} {
  const changePercent = Math.abs(((newBudget - currentBudget) / currentBudget) * 100);
  const requiresApproval = changePercent > threshold;
  
  return {
    isValid: newBudget >= 0,
    requiresApproval,
    changePercent,
  };
}

// ============================================================================
// ALERT GENERATION
// ============================================================================

export function shouldGenerateCostAlert(
  progressPercent: number,
  completionPercent: number
): {
  shouldAlert: boolean;
  severity: 'warning' | 'critical';
  message: string;
} | null {
  const difference = progressPercent - completionPercent;
  
  if (progressPercent > 100) {
    return {
      shouldAlert: true,
      severity: 'critical',
      message: `Cost budget exceeded by ${formatPercent(progressPercent - 100)}`,
    };
  }
  
  if (difference > 20) {
    return {
      shouldAlert: true,
      severity: 'warning',
      message: `Cost budget ${formatPercent(progressPercent)} consumed with ${formatPercent(100 - completionPercent)} of work remaining`,
    };
  }
  
  return null;
}

export function shouldGenerateMarginAlert(
  actualMargin: number,
  targetMargin: number,
  threshold = 5
): {
  shouldAlert: boolean;
  severity: 'warning' | 'critical';
  message: string;
} | null {
  const difference = targetMargin - actualMargin;
  
  if (difference > threshold * 2) {
    return {
      shouldAlert: true,
      severity: 'critical',
      message: `Margin ${formatPercent(actualMargin)} is ${formatPercent(difference)} below target`,
    };
  }
  
  if (difference > threshold) {
    return {
      shouldAlert: true,
      severity: 'warning',
      message: `Margin has decreased to ${formatPercent(actualMargin)} (${formatPercent(difference)} below target)`,
    };
  }
  
  return null;
}

// ============================================================================
// CHART DATA HELPERS
// ============================================================================

export function generateMonthLabels(startDate: Date, months: number): string[] {
  const labels: string[] = [];
  const date = new Date(startDate);
  
  for (let i = 0; i < months; i++) {
    labels.push(
      date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
    );
    date.setMonth(date.getMonth() + 1);
  }
  
  return labels;
}

export function aggregateByMonth<T extends { date: string; value: number }>(
  data: T[]
): { month: string; value: number }[] {
  const monthMap = new Map<string, number>();
  
  data.forEach((item) => {
    const date = new Date(item.date);
    const monthKey = date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
    
    const currentValue = monthMap.get(monthKey) || 0;
    monthMap.set(monthKey, currentValue + item.value);
  });
  
  return Array.from(monthMap.entries()).map(([month, value]) => ({
    month,
    value,
  }));
}

// ============================================================================
// COLOR HELPERS
// ============================================================================

export function getStatusColor(status: 'green' | 'amber' | 'red' | string): string {
  switch (status) {
    case 'green':
      return 'text-success-600';
    case 'amber':
      return 'text-warning-600';
    case 'red':
      return 'text-error-600';
    default:
      return 'text-neutral-600';
  }
}

export function getStatusBgColor(status: 'green' | 'amber' | 'red' | string): string {
  switch (status) {
    case 'green':
      return 'bg-success-100';
    case 'amber':
      return 'bg-warning-100';
    case 'red':
      return 'bg-error-100';
    default:
      return 'bg-neutral-100';
  }
}

export function getStatusBorderColor(status: 'green' | 'amber' | 'red' | string): string {
  switch (status) {
    case 'green':
      return 'border-success-600';
    case 'amber':
      return 'border-warning-600';
    case 'red':
      return 'border-error-600';
    default:
      return 'border-neutral-600';
  }
}

export function getProgressColor(percent: number): string {
  if (percent < 80) return 'bg-success-500';
  if (percent < 100) return 'bg-warning-500';
  return 'bg-error-500';
}
